import { Injectable } from '@angular/core';
import { FeeChargeRow } from '../models/fee-config';

export interface FeeChargeRowErrors {
  chargeId?: string;
  eventType?: string;
  eventSubValue?: string;
  monthlyCustomDay?: string;
  q1Month?: string;
  q2Month?: string;
  q3Month?: string;
  q4Month?: string;
  h1Month?: string;
  h2Month?: string;
  minMax?: string;
  duplicate?: string;
}

@Injectable({ providedIn: 'root' })
export class FeeChargeValidatorService {

  /**
   * @param eventTypeMap  Map of eventValue → scheduleType sourced from the backend.
   *                      Validation is driven by scheduleType, not hardcoded event names.
   *                      Unknown / missing scheduleType → no schedule validation (safe default).
   */
  validateRow(
    row: FeeChargeRow,
    allRows: FeeChargeRow[] = [],
    index: number = -1,
    eventTypeMap: Map<string, string> = new Map(),
  ): FeeChargeRowErrors {
    const err: FeeChargeRowErrors = {};

    // ── Charge selection ───────────────────────────────────────────────────
    if (!row.chargeId) {
      err.chargeId = 'Fee/Charge is required';
    }

    // ── Event type ─────────────────────────────────────────────────────────
    if (!row.eventType) {
      err.eventType = 'Event type is required';
      return err;
    }

    // ── Duplicate charge + event type across rows ──────────────────────────
    if (row.chargeId && index >= 0) {
      const isDuplicate = allRows.some((r, i) =>
        i !== index && r.chargeId === row.chargeId && r.eventType === row.eventType
      );
      if (isDuplicate) {
        err.duplicate = `This charge already has a "${row.eventType}" event configured`;
      }
    }

    // ── Schedule sub-value validation (driven by backend scheduleType) ─────
    // Switch on the scheduleType the backend assigned to this event.
    // If scheduleType is unknown or not in the set of scheduled types, we
    // skip sub-value validation — transaction-driven events fall through here.
    const scheduleType = eventTypeMap.get(row.eventType) ?? '';

    switch (scheduleType) {

      case 'TIME':
        if (!row.eventSubValue) {
          err.eventSubValue = 'Time of day is required';
        }
        break;

      case 'DAY_OF_WEEK':
        if (!row.eventSubValue) {
          err.eventSubValue = 'Day of week is required';
        }
        break;

      case 'DAY_OF_MONTH':
        if (!row.eventSubValue) {
          err.eventSubValue = 'Schedule option is required';
        } else if (row.eventSubValue === 'CUSTOM') {
          const day = parseInt(row.monthlyCustomDay, 10);
          if (!row.monthlyCustomDay || isNaN(day) || day < 1 || day > 31) {
            err.monthlyCustomDay = 'Enter a valid day between 1 and 31';
          }
        }
        break;

      case 'QUARTER_MONTHS':
        if (!row.q1Month) err.q1Month = 'Q1 month is required';
        if (!row.q2Month) err.q2Month = 'Q2 month is required';
        if (!row.q3Month) err.q3Month = 'Q3 month is required';
        if (!row.q4Month) err.q4Month = 'Q4 month is required';
        break;

      case 'HALF_MONTHS':
        if (!row.h1Month) err.h1Month = 'First-half month is required';
        if (!row.h2Month) err.h2Month = 'Second-half month is required';
        break;

      case 'MONTH':
        if (!row.eventSubValue) {
          err.eventSubValue = 'Month is required';
        }
        break;

      // 'NONE', unknown, or missing → transaction-driven, no schedule needed
    }

    // ── Min / Max validation (non-FLAT only, user edits these) ───────────
    if (row.chargeType && row.chargeType !== 'FLAT') {
      const minStr = String(row.minimumValue || '').replace(/[^0-9.]/g, '');
      const maxStr = String(row.maximumValue || '').replace(/[^0-9.]/g, '');
      const min = parseFloat(minStr);
      const max = parseFloat(maxStr);

      if (!minStr || isNaN(min)) {
        err.minMax = 'Minimum value is required';
      } else if (!maxStr || isNaN(max)) {
        err.minMax = 'Maximum value is required';
      } else if (min > max) {
        err.minMax = 'Minimum value cannot exceed maximum value';
      }
    }

    return err;
  }

  validateAll(rows: FeeChargeRow[], eventTypeMap: Map<string, string> = new Map()): FeeChargeRowErrors[] {
    return rows.map((row, i) => this.validateRow(row, rows, i, eventTypeMap));
  }

  isValid(rows: FeeChargeRow[], eventTypeMap: Map<string, string> = new Map()): boolean {
    return this.validateAll(rows, eventTypeMap).every(e => Object.keys(e).length === 0);
  }
}
